import { z } from 'zod';
import {
  FleetMachineApprovalSchema,
  FleetMachineViewSchema,
  type FleetMachineApproval,
} from './fleet-machines.schemas';
import { FleetHostHealthSchema, FleetHostThrottleSchema } from './fleet-health.schemas';

/**
 * A profile's lifecycle, as an enrolled machine reports it (IMM-910.2,
 * ADR 0047). The only typed field of a status payload — `title`, `fields` and
 * `badges` are validated for shape and stored verbatim.
 */
export const FLEET_PROFILE_STATES = ['pending', 'idle', 'working', 'waiting-on-human', 'stopped'] as const;
export const FleetProfileStateSchema = z.enum(FLEET_PROFILE_STATES);
export type FleetProfileState = (typeof FLEET_PROFILE_STATES)[number];

/**
 * The numeric ceilings a status payload is validated against (IMM-910.3, D8).
 * Extracted so the producer (the fleet-agent toolkit) truncates against the exact
 * numbers this schema enforces, rather than a remembered copy that silently
 * drifts the day one of these widens.
 */
export const FLEET_STATUS_LIMITS = {
  profile: 64,
  worker: 128,
  title: 200,
  fieldLabel: 200,
  fieldValue: 200,
  href: 500,
  fields: 20,
  badge: 32,
  badges: 6,
  commit: 200,
} as const;

/**
 * What the host last saw of an engine account. `logged-out` and `usage-limit`
 * come from a round that never reached the model (the lane is out of
 * service); `ok` is an account with a login on disk and no such round;
 * `unknown` has neither.
 */
export const FLEET_ACCOUNT_AUTH_STATES = ['ok', 'logged-out', 'usage-limit', 'unknown'] as const;
export const FleetAccountAuthSchema = z.enum(FLEET_ACCOUNT_AUTH_STATES);
export type FleetAccountAuth = (typeof FLEET_ACCOUNT_AUTH_STATES)[number];

/** The ceilings an account entry is validated against; the host truncates to the same numbers. */
export const FLEET_ACCOUNT_LIMITS = { accounts: 8, configDir: 200, email: 200, engine: 32, lanes: 32 } as const;

const FractionSchema = z.number().min(0).max(1);

/** What the plan has left, as the last run's output said. Fractions 0–1; `null` is a window nobody reported. */
export const FleetAccountQuotaSchema = z.object({
  session: FractionSchema.nullable(),
  week: FractionSchema.nullable(),
  limited: z.boolean(),
  readAt: z.iso.datetime(),
  /** When the weekly window refills; absent from an agent that does not report it. */
  weekResetsAt: z.iso.datetime().nullish(),
  /** When the session (5h) window refills; absent from an agent that does not report it. */
  sessionResetsAt: z.iso.datetime().nullish(),
  /** When the engine's own refusal lifts, as the engine said; absent from an agent that does not report it. */
  limitedUntil: z.iso.datetime().nullish(),
});
export type FleetAccountQuota = z.infer<typeof FleetAccountQuotaSchema>;

/**
 * One engine account a host's lanes run on, as the host reports it each tick
 * (fleet-agent 0.18.0). One entry per distinct config dir in use. Typed,
 * unlike a lane's `fields`: the dashboard and every other host's preview read
 * it, and a renderer that guesses at label text cannot.
 *
 * `configDir` is home-relative as the host wrote it (`~/.claude-work`);
 * `null` is the engine's default login.
 */
export const FleetAccountSchema = z.object({
  engine: z.string().min(1).max(FLEET_ACCOUNT_LIMITS.engine),
  configDir: z.string().min(1).max(FLEET_ACCOUNT_LIMITS.configDir).nullable(),
  email: z.string().min(1).max(FLEET_ACCOUNT_LIMITS.email).nullable(),
  auth: FleetAccountAuthSchema,
  quota: FleetAccountQuotaSchema.nullable(),
  lanes: z.array(z.string().min(1).max(FLEET_STATUS_LIMITS.profile)).max(FLEET_ACCOUNT_LIMITS.lanes),
  /**
   * When this host's own evidence says the account takes work again — a spent window, the engine's refusal, or a
   * 429 the host hit. `null` is not spent; absent from an agent that does not report it. It ignores the operator's
   * weekly cap and a lost login.
   */
  limitedUntil: z.iso.datetime().nullish(),
});
export type FleetAccount = z.infer<typeof FleetAccountSchema>;

/**
 * The engine a host runs each lane on, as the wire carries it (fleet-agent 0.19.0, its ADR 0004): the `engine`
 * command that sets it from the fleet page, and the per-lane report on every tick. Mirrors fleet-agent's
 * `domain/fleet/engines.ts` by hand — keep both identical.
 */
export const FLEET_ENGINE_NAMES = ['claude', 'codex', 'cursor'] as const;
export type FleetEngineName = (typeof FLEET_ENGINE_NAMES)[number];

/**
 * Where a lane's effective engine came from, first that says anything winning: this run's flags, the host's
 * override for this project's lane, the host's lane override, the host default, the project lane's
 * `round.stepEngine`, the built-in default (claude).
 */
export const FLEET_ENGINE_SOURCES = [
  'flag',
  'host-project-lane',
  'host-lane',
  'host-default',
  'project',
  'built-in',
] as const;
export type FleetEngineSource = (typeof FLEET_ENGINE_SOURCES)[number];

/** A model id as a person types it: plain characters, no spaces, nothing a shell would read. */
export const FLEET_MODEL_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:/[\]-]{0,99}$/;

/** A lane name, as a project declares it. */
export const FLEET_LANE_PATTERN = /^[a-z][a-z0-9-]{0,63}$/;

/** A project name, as a host's fleet config declares it (fleet-agent `domain/fleet/projects.ts`). */
export const FLEET_PROJECT_PATTERN = /^[a-z][a-z0-9-]{0,31}$/;

export const FleetEngineNameSchema = z.enum(FLEET_ENGINE_NAMES);
export const FleetEngineSourceSchema = z.enum(FLEET_ENGINE_SOURCES);
export const FleetModelIdSchema = z.string().regex(FLEET_MODEL_ID_PATTERN);
export const FleetLaneNameSchema = z.string().regex(FLEET_LANE_PATTERN);
export const FleetProjectNameSchema = z.string().regex(FLEET_PROJECT_PATTERN);

/**
 * The engine and models a lane's next round runs on, and where that came from. Models are resolved — the
 * adapter's default filled in — so the page shows what runs without knowing any adapter. `orchestrator` differs
 * from `engine` only when the project's `stepEngine` (which names the steps alone) chose it.
 */
export const FleetLaneEngineSchema = z.object({
  engine: FleetEngineNameSchema,
  orchestrator: FleetEngineNameSchema,
  planModel: FleetModelIdSchema,
  implementModel: FleetModelIdSchema,
  source: FleetEngineSourceSchema,
});
export type FleetLaneEngine = z.infer<typeof FleetLaneEngineSchema>;

/**
 * A lane under cross-engine rotation (IMM-1452). `pin`: the engine its rounds are restricted to, `null` for any.
 * `running`: the engine of the round in flight, `null` when none runs. Absent from a host that predates rotation.
 */
export const FleetLaneEngineRotationSchema = z.object({
  pin: FleetEngineNameSchema.nullable(),
  running: FleetEngineNameSchema.nullable(),
});
export type FleetLaneEngineRotation = z.infer<typeof FleetLaneEngineRotationSchema>;

// The established repo pattern for an http(s)-only URL — see
// identity/organizations.schemas.ts. Bare z.url() accepts `javascript:`, which
// IMM-910.7's generic renderer would put on an anchor unvalidated.
const HttpUrlSchema = z.url().refine((value) => /^https?:\/\//i.test(value), { message: 'must be an http(s) URL' });

/** Rendered verbatim. The backend validates the envelope, never the meaning. */
export const FleetStatusFieldSchema = z.object({
  label: z.string().min(1).max(FLEET_STATUS_LIMITS.fieldLabel),
  value: z.string().min(1).max(FLEET_STATUS_LIMITS.fieldValue),
  href: HttpUrlSchema.max(FLEET_STATUS_LIMITS.href).optional(),
});
export type FleetStatusField = z.infer<typeof FleetStatusFieldSchema>;

export const FleetProfileStatusSchema = z.object({
  profile: z.string().min(1).max(FLEET_STATUS_LIMITS.profile),
  /**
   * The worker that owns this lane. Optional on the way IN (an older agent
   * does not send it) and nullable on the way OUT — the dashboard needs it to
   * read the lane's stored log lines, which are keyed by worker and not by
   * profile (IMM-918).
   */
  worker: z.string().min(1).max(FLEET_STATUS_LIMITS.worker).nullish(),
  state: FleetProfileStateSchema,
  title: z.string().max(FLEET_STATUS_LIMITS.title).nullable(),
  fields: z.array(FleetStatusFieldSchema).max(FLEET_STATUS_LIMITS.fields),
  badges: z.array(z.string().min(1).max(FLEET_STATUS_LIMITS.badge)).max(FLEET_STATUS_LIMITS.badges),
  reportedAt: z.iso.datetime(),
  /**
   * How many hosts may run this profile at once, as the reporting host's
   * registry says (the fleet-agent toolkit (`profiles.ts`)). Optional on the way IN (an
   * older agent does not send it) and nullable on the way OUT — `null` is
   * both "unlimited" and "never reported", which this cap treats alike.
   */
  maxWorkers: z.number().int().positive().nullish(),
  /** The engine the lane's next round runs on (fleet-agent 0.19.0); absent from a host that predates it, `null` unknown. */
  engine: FleetLaneEngineSchema.nullish(),
  /** The lane's pin and the engine of its round in flight (IMM-1452); absent from a host that predates rotation. */
  engineRotation: FleetLaneEngineRotationSchema.nullish(),
});
export type FleetProfileStatus = z.infer<typeof FleetProfileStatusSchema>;

/** A host at or above its weekly limit: the highest usage among its accounts and when the last holding window refills. */
export const FleetWeeklyHoldSchema = z.object({
  usagePercent: z.number().int().min(0).max(100),
  resetsAt: z.iso.datetime().nullable(),
});

export const FleetMachineOverviewSchema = FleetMachineViewSchema.extend({
  /** Always `null` since IMM-1450: the per-machine weekly limit is retired. Kept so older tabs and app builds still parse. */
  weeklyHold: FleetWeeklyHoldSchema.nullable(),
  stale: z.boolean(),
  /** The latest reading; `null` when the machine never reported one, absent from an older backend. */
  health: FleetHostHealthSchema.nullish(),
  profiles: z.array(FleetProfileStatusSchema),
  /** What the machine's last tick said of its accounts; absent until a 0.18.0 agent reports them. */
  accounts: z.array(FleetAccountSchema).max(FLEET_ACCOUNT_LIMITS.accounts).optional(),
  /**
   * The projects the machine's workers reported a lane for this past week (OPS-69), sorted — the projects a
   * `run-command` may name on it (fleet-agent ADR 0016). Absent from a backend without a lane registry.
   */
  projects: z.array(FleetProjectNameSchema).optional(),
});
export type FleetMachineOverview = z.infer<typeof FleetMachineOverviewSchema>;

export const FleetOverviewResponseSchema = z.object({ data: z.array(FleetMachineOverviewSchema) });
export type FleetOverviewResponse = z.infer<typeof FleetOverviewResponseSchema>;

/**
 * How long a machine may go unheard before the fleet stops counting on what it
 * last said — 2× the agent's 5-minute report interval (IMM-910.2). Shared so
 * the overview's `stale` flag, the capacity rule below and the CLI's local
 * snapshot all expire at the same moment.
 */
export const FLEET_STALE_AFTER_MS = 10 * 60 * 1000;

/**
 * The slice of a machine the capacity rule reads. Structural, so the backend's
 * domain rows, the overview response and a test fixture all fit without a
 * mapping step.
 */
export type FleetCapacityMachine = {
  readonly id: string;
  readonly name: string;
  readonly approval: FleetMachineApproval;
  readonly lastSeenAt: string | Date;
  readonly profiles: ReadonlyArray<{
    readonly profile: string;
    readonly state: FleetProfileState;
    readonly maxWorkers?: number | null | undefined;
  }>;
};

/** Where a capped profile stands, seen from one machine. */
export type FleetProfileCapacity = {
  readonly maxWorkers: number;
  /** The OTHER machines running it — the machine the question is asked for is never in this list. */
  readonly runningOn: ReadonlyArray<{ readonly machineId: string; readonly name: string }>;
};

function countsTowardsCapacity(machine: FleetCapacityMachine, now: Date): boolean {
  if (machine.approval !== 'approved') return false;

  return now.getTime() - new Date(machine.lastSeenAt).getTime() <= FLEET_STALE_AFTER_MS;
}

/**
 * The per-profile worker cap (IMM-979), relative to `forMachineId`, or `null`
 * when nobody the fleet can currently hear reports a cap for the profile.
 *
 * Only approved, non-stale machines count — both for the cap and for who is
 * running: an unapproved machine cannot be commanded and a stale one is not
 * heard, so neither may hold a slot. When machines disagree about the cap
 * (a host on an older registry), the SMALLEST reported one wins, because the
 * cap only ever exists to keep the fleet below a number.
 *
 * Best-effort by construction: it reads the last reports, so two starts
 * within one report tick can both see a free slot.
 */
export function fleetProfileCapacity(
  machines: ReadonlyArray<FleetCapacityMachine>,
  profile: string,
  forMachineId: string,
  now: Date,
): FleetProfileCapacity | null {
  const heard = machines.filter((machine) => countsTowardsCapacity(machine, now));
  const caps = heard.flatMap((machine) =>
    machine.profiles
      .filter((entry) => entry.profile === profile)
      .map((entry) => entry.maxWorkers)
      .filter((cap): cap is number => typeof cap === 'number'),
  );

  if (caps.length === 0) return null;

  const runningOn = heard
    .filter((machine) => machine.id !== forMachineId)
    .filter((machine) => machine.profiles.some((entry) => entry.profile === profile && entry.state !== 'stopped'))
    .map((machine) => ({ machineId: machine.id, name: machine.name }));

  return { maxWorkers: Math.min(...caps), runningOn };
}

/** Whether one more machine starting the profile would exceed its cap. `null` (no cap) never is. */
export function isAtFleetCapacity(capacity: FleetProfileCapacity | null): boolean {
  return capacity !== null && capacity.runningOn.length >= capacity.maxWorkers;
}

/**
 * One lane of a peer machine, as a MACHINE reads it (IMM-939).
 *
 * The lean sibling of {@link FleetProfileStatusSchema}: `fields` and `badges`
 * are a dashboard's rendering material and a terminal has no use for them, so
 * they are not sent. What is here is what `fleet-agent agent fleet machines` prints
 * and what lets the CLI refuse an unknown lane before a verb travels.
 */
export const FleetPeerLaneSchema = z.object({
  profile: z.string(),
  worker: z.string().nullable(),
  state: FleetProfileStateSchema,
  title: z.string().nullable(),
  reportedAt: z.iso.datetime(),
});
export type FleetPeerLane = z.infer<typeof FleetPeerLaneSchema>;

/**
 * A machine of this fleet, as another MACHINE reads it (IMM-939).
 *
 * Deliberately NOT {@link FleetMachineOverviewSchema}. That one is the
 * operator's, and it carries `approvedByName` — an admin's full name, which
 * is identity-module data and has no business reaching a credential whose
 * whole guarantee is that it reaches fleet data and nothing else. This view
 * carries the machine's own facts only.
 *
 * `mine` marks the calling machine, the same way the live-jobs read does.
 */
export const FleetPeerSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  hostname: z.string(),
  platform: z.string(),
  agentVersion: z.string(),
  approval: FleetMachineApprovalSchema,
  checkoutCommit: z.string().nullable(),
  lastSeenAt: z.iso.datetime(),
  /** Derived from `lastSeenAt` on read, never stored — a machine that returns is fresh immediately. */
  stale: z.boolean(),
  mine: z.boolean(),
  lanes: z.array(FleetPeerLaneSchema),
  /** From the latest reading; `null` when it claims work or never reported, absent from an older backend. */
  throttle: FleetHostThrottleSchema.nullish(),
  /** What the machine's last tick said of its accounts; absent from a backend that predates them. */
  accounts: z.array(FleetAccountSchema).max(FLEET_ACCOUNT_LIMITS.accounts).optional(),
});
export type FleetPeer = z.infer<typeof FleetPeerSchema>;

export const ListFleetPeersResponseSchema = z.object({ data: z.array(FleetPeerSchema) });
export type ListFleetPeersResponse = z.infer<typeof ListFleetPeersResponseSchema>;

/**
 * NOT an HTTP contract — the relay wire format, shared so the producer
 * (IMM-910.3) and this projection parse through one definition. The signing
 * key is deliberately absent: it is the nostr event's own `pubkey`, never a
 * field a publisher could choose.
 */
export const FLEET_STATUS_EVENT_PREFIX = 'fleet-status ';

export const FleetStatusEventSchema = z.object({
  v: z.literal(1),
  profile: z.string().min(1).max(FLEET_STATUS_LIMITS.profile),
  /**
   * The worker that owns this lane. The dashboard reads a lane's stored log
   * lines by worker, not by profile, so without this the page can see a lane
   * and still not name whose lines belong to it (IMM-918). `null` when the
   * lane has no worker record yet.
   */
  worker: z.string().min(1).max(FLEET_STATUS_LIMITS.worker).nullable().default(null),
  /**
   * The HOST's checked-out HEAD (`<short sha> <subject>`), not this profile's:
   * every lane on a machine shares one primary checkout, so the agent reads it
   * once per sweep and every profile's event carries the same value. The
   * projection stores it on the machine row, which is why it is a typed field
   * here rather than one more opaque entry in `fields` — a per-lane copy of a
   * host fact renders four times and can never be shown once. `null` means
   * "cannot say" (a checkout mid-rebase, no git), never "no commit".
   */
  commit: z.string().min(1).max(FLEET_STATUS_LIMITS.commit).nullable().default(null),
  state: FleetProfileStateSchema,
  title: z.string().max(FLEET_STATUS_LIMITS.title).nullable().default(null),
  fields: z.array(FleetStatusFieldSchema).max(FLEET_STATUS_LIMITS.fields).default([]),
  badges: z.array(z.string().min(1).max(FLEET_STATUS_LIMITS.badge)).max(FLEET_STATUS_LIMITS.badges).default([]),
  reportedAt: z.iso.datetime(),
});
export type FleetStatusEvent = z.infer<typeof FleetStatusEventSchema>;
